import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Check } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { MINIMAL_FILTER_FIELD_CLASS, FILTER_OPTION_CHECKBOX_CLASS, FILTER_OPTION_ROW_CLASS } from '@/components/filters/filter-styles'

export interface FilterOption {
  value: string
  label: string
}

export type TextFilterOperator = 'is' | 'is_not' | 'contains' | 'does_not_contain'

export const TEXT_FILTER_OPERATOR_OPTIONS: FilterOption[] = [
  { value: 'is', label: 'Is' },
  { value: 'is_not', label: 'Is not' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
]

const OPTION_SCROLL_THRESHOLD = 6
const OPTION_SEARCH_THRESHOLD = 10
const TEXT_FILTER_DEBOUNCE_MS = 400

interface FilterMenuFieldProps {
  label: string
  icon?: LucideIcon
  children: ReactNode
  contentClassName?: string
}

export function FilterMenuField({
  label,
  icon: Icon,
  children,
  contentClassName,
}: FilterMenuFieldProps) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="gap-2">
        {Icon ? <Icon className="h-4 w-4 text-muted-foreground" aria-hidden /> : null}
        {label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className={cn('w-56', contentClassName)}>
        {children}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}

interface SingleFilterOptionListProps {
  type: 'single'
  options: FilterOption[]
  value: string
  onValueChange: (value: string) => void
  searchPlaceholder?: string
  emptyLabel?: string
  searchQuery?: string
  onSearchQueryChange?: (query: string) => void
}

interface MultiFilterOptionListProps {
  type: 'multi'
  options: FilterOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  searchPlaceholder?: string
  emptyLabel?: string
  searchQuery?: string
  onSearchQueryChange?: (query: string) => void
}

type FilterOptionListProps = SingleFilterOptionListProps | MultiFilterOptionListProps

export function FilterOptionList(props: FilterOptionListProps) {
  const [internalQuery, setInternalQuery] = useState('')
  const query = props.searchQuery ?? internalQuery
  const setQuery = props.onSearchQueryChange ?? setInternalQuery
  const searchable =
    props.options.length > OPTION_SEARCH_THRESHOLD || Boolean(props.searchPlaceholder)
  const scrollable = props.options.length > OPTION_SCROLL_THRESHOLD

  const filteredOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase()
    if (!trimmed) return props.options
    return props.options.filter((option) => option.label.toLowerCase().includes(trimmed))
  }, [props.options, query])

  const listClassName = cn(scrollable && 'max-h-52 overflow-y-auto pr-1')

  const searchInput = searchable ? (
    <Input
      type="search"
      value={query}
      onChange={(event) => setQuery(event.target.value)}
      onKeyDown={(event) => event.stopPropagation()}
      placeholder={props.searchPlaceholder ?? 'Search options...'}
      className={MINIMAL_FILTER_FIELD_CLASS}
      autoComplete="off"
    />
  ) : null

  if (props.type === 'single') {
    return (
      <>
        {searchInput}
        <div role="radiogroup" className={listClassName}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const selected = props.value === option.value
              return (
                <button
                  key={option.value || '__all__'}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={cn(FILTER_OPTION_ROW_CLASS, selected && 'bg-accent text-accent-foreground')}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => props.onValueChange(option.value)}
                >
                  <span className="truncate">{option.label}</span>
                  {selected ? <Check className="ml-auto h-4 w-4 shrink-0" aria-hidden /> : null}
                </button>
              )
            })
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">{props.emptyLabel ?? 'No options found'}</div>
          )}
        </div>
      </>
    )
  }

  const selected = new Set(props.value)

  return (
    <>
      {searchInput}
      <div className={cn('w-full text-left', listClassName)}>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const checked = selected.has(option.value)
            return (
              <button
                key={option.value}
                type="button"
                role="checkbox"
                aria-checked={checked}
                className={FILTER_OPTION_ROW_CLASS}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  const next = new Set(props.value)
                  if (checked) {
                    next.delete(option.value)
                  } else {
                    next.add(option.value)
                  }
                  props.onValueChange(Array.from(next))
                }}
              >
                <Checkbox checked={checked} tabIndex={-1} className={FILTER_OPTION_CHECKBOX_CLASS} aria-hidden />
                <span className="min-w-0 truncate text-left">{option.label}</span>
              </button>
            )
          })
        ) : (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">{props.emptyLabel ?? 'No options found'}</div>
        )}
      </div>
    </>
  )
}

interface TextFilterControlProps {
  operator: TextFilterOperator
  value: string
  onOperatorChange?: (operator: TextFilterOperator) => void
  onValueChange: (value: string) => void
  suggestions?: FilterOption[]
  inputId?: string
  inputPlaceholder?: string
  suggestionLabel?: string
  debounceMs?: number
  /** When false, hides the operator select (defaults to contains / is semantics). */
  showOperator?: boolean
}

export function TextFilterControl({
  operator,
  value,
  onOperatorChange,
  onValueChange,
  suggestions = [],
  inputId,
  inputPlaceholder,
  suggestionLabel = 'Suggestions',
  debounceMs = TEXT_FILTER_DEBOUNCE_MS,
  showOperator = true,
}: TextFilterControlProps) {
  const [draftValue, setDraftValue] = useState(value)
  const trimmedDraft = draftValue.trim().toLowerCase()

  const visibleSuggestions = useMemo(() => {
    if (!trimmedDraft) return []
    return suggestions
      .filter((suggestion) => suggestion.label.toLowerCase().includes(trimmedDraft))
      .filter((suggestion) => suggestion.value !== draftValue)
      .slice(0, 6)
  }, [draftValue, suggestions, trimmedDraft])

  useEffect(() => {
    setDraftValue(value)
  }, [value])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (draftValue !== value) {
        onValueChange(draftValue)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [debounceMs, draftValue, onValueChange, value])

  return (
    <div className="space-y-2 p-4" onKeyDown={(event) => event.stopPropagation()}>
      {showOperator ? (
        <Select
          value={operator}
          onValueChange={(next) => onOperatorChange?.(next as TextFilterOperator)}
        >
          <SelectTrigger className={MINIMAL_FILTER_FIELD_CLASS}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TEXT_FILTER_OPERATOR_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      <Input
        id={inputId}
        value={draftValue}
        onChange={(event) => setDraftValue(event.target.value)}
        onKeyDown={(event) => {
          event.stopPropagation()
          if (event.key === 'Enter') {
            onValueChange(draftValue)
          }
        }}
        placeholder={inputPlaceholder ?? 'Filter...'}
        className={MINIMAL_FILTER_FIELD_CLASS}
        autoComplete="off"
      />
      {visibleSuggestions.length > 0 ? (
        <div className="space-y-1">
          <div className="px-1 text-xs text-muted-foreground">{suggestionLabel}</div>
          <div className="max-h-40 overflow-y-auto">
            {visibleSuggestions.map((suggestion) => (
              <button
                key={suggestion.value}
                type="button"
                className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                onClick={() => {
                  setDraftValue(suggestion.value)
                  onValueChange(suggestion.value)
                }}
              >
                <span className="truncate">{suggestion.label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
